/**
 * settingsCache.js
 *
 * Singleton cache for /api/settings so that the 20+ components on the page
 * that read settings make exactly ONE network request per session instead of
 * each firing their own fetch.
 *
 * Layers:
 *   1. In-memory  – instant, lives for the JS module lifetime (tab open)
 *   2. sessionStorage – survives React remounts, 5-minute TTL
 *   3. Network fetch  – only when both caches are cold/stale
 */

const STORAGE_KEY = 'svb-settings-v2';
const TTL_MS      = 5 * 60 * 1000; // 5 minutes

let _memory  = null; // in-memory cache
let _promise = null; // in-flight request dedup

/** Return settings object (empty {} on error). Never throws. */
export const getSettings = () => {
  // ── Layer 1: in-memory ──────────────────────────────────────
  if (_memory !== null) return Promise.resolve(_memory);

  // ── Layer 2: sessionStorage ──────────────────────────────────
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts < TTL_MS) {
        _memory = data;
        return Promise.resolve(_memory);
      }
    }
  } catch {}

  // ── Layer 3: network (deduped via shared promise) ────────────
  if (_promise) return _promise;

  const api = process.env.REACT_APP_BACKEND_URL || '';
  _promise = fetch(`${api}/api/settings`)
    .then(r => r.ok ? r.json() : {})
    .then(data => {
      _memory  = data || {};
      _promise = null;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), data: _memory }));
      } catch {}
      return _memory;
    })
    .catch(() => {
      _promise = null;
      _memory  = {};
      return _memory;
    });

  return _promise;
};

/**
 * Force a fresh fetch on next call.
 * Call this after admin saves settings so the page reflects changes immediately.
 */
export const invalidateSettings = () => {
  _memory  = null;
  _promise = null;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
};
